import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { useQueryParams } from 'global/hooks/useQuery';
import FormsSpinner from 'components/common/general/FormSpinner';
import { toJS } from 'mobx';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import HeaderWithBcBlock from 'components/common/headers/HeaderWithBcBlock';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import { useHistory } from 'react-router-dom';
import RateSegmentCard from '../RateSegmentCard';
import { createStyles } from './styles';
import ProductDetailsModal from '../ProductDetailsModal';

const RateSegmentsContent = observer((): JSX.Element => {
  const classes = createStyles();

  const history = useHistory();
  const query = useQueryParams();
  const cartId = query.get('cartId') || null;

  const {
    rateSegmentsStore: {
      loading,
      currentEffectiveDate,
      currentZipCode,
      showProductDetailsModal,
      domainConfig,
      cart,
      isResetModalOpen,
      setShowResetModal,
      resetProgress,
      collectInitialData,
      setShowProductDetailsModal,
      updateRateSegment,
      setCurrentRateSegment,
    },
  } = useRootStore();

  // Save middle rateSegment value for reset modal
  // Value in store will be updated only after modal reset submit
  const [currentRateSegment, setRateSegment] = useState<RateSegment>('GROUP');

  useEffect(() => {
    if (cartId) void collectInitialData(cartId);
  }, []);

  const convertedDomainConfig = toJS(domainConfig);
  const convertedCart = toJS(cart);

  const onContinue = (externalRedirectURL: string): void => {
    if (externalRedirectURL) {
      window.location.href = externalRedirectURL;
    } else {
      setShowProductDetailsModal(true);
    }
  };

  const CardsList = Object.keys(convertedDomainConfig)
    .filter((rateSegment) => convertedDomainConfig[rateSegment]?.eStoreVisible)
    .map((rateSegment) => {
      const config = convertedDomainConfig[rateSegment];
      const externalRedirectURL = config?.eStoreRedirect || '';

      return (
        <RateSegmentCard
          key={rateSegment}
          rateSegment={rateSegment as RateSegment}
          descriptionList={config?.eStoreNotes || []}
          onContinue={async (): Promise<void> => {
            const currentCartId = convertedCart.id || cartId;
            //  Same rateSegment
            if (currentCartId && convertedCart.rateSegment === rateSegment) {
              onContinue(externalRedirectURL);
            }
            // Different rateSegments
            if (currentCartId && convertedCart.rateSegment !== rateSegment) {
              // Empty cart
              if (convertedCart.cartItems.length === 0) {
                await updateRateSegment(currentCartId, rateSegment as RateSegment);
                onContinue(externalRedirectURL);
              } else {
                setRateSegment(rateSegment as RateSegment);
                setShowResetModal(true);
              }
            }
          }}
        />
      );
    });

  return (
    <>
      <HeaderWithBcBlock
        title="Choose a Plan Type"
        subtitle="And we will show a selection tailored to your needs"
        bcBlockText={`Effective date: ${normDateToShortAmericanDate(currentEffectiveDate)}, Zip: ${currentZipCode}`}
        loading={loading}
        onRedirect={(): void => {
          // This variant is used instead of history.push to simulate home link behavior
          window.location.href = '/browse-info';
        }}
      />
      <div className={classes.listWrapper}>
        {loading ? (
          <FormsSpinner display={loading} semiTransparentBg={false} />
        ) : (
          <ul className={classes.cardsList}>{CardsList}</ul>
        )}
      </div>
      <ProductDetailsModal
        cartId={convertedCart.id}
        show={showProductDetailsModal}
        onClose={(): void => setShowProductDetailsModal(false)}
      />
      <ActionModal
        show={isResetModalOpen}
        onClose={(): void => {
          setShowResetModal(false);
        }}
        modalType={ModalType.NOTIFICATION}
        closeButtonText="Stay on Page"
        actionButtonText="Start over"
        onAction={async (): Promise<void> => {
          setCurrentRateSegment(currentRateSegment);
          const newCart = await resetProgress();
          if (newCart) {
            history.push(`/rate-segments?cartId=${newCart.id}&cartType=rate-segment`);
            const config = convertedDomainConfig[newCart.rateSegment];
            const externalRedirectURL = config?.eStoreRedirect || '';
            onContinue(externalRedirectURL);
          }
        }}
        title="Reset Progress"
        subtitle="You've already added products for another rate segment. Do you want to start over with new product type? Previous progress will be cleared."
      />
    </>
  );
});

export default RateSegmentsContent;
